import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface CashFlowEntryModalProps {
  type: 'credit' | 'debit';
  clients: { id: string; name: string }[];
  projects: { id: string; name: string }[];
  onClose: () => void;
  onSave: (data: {
    flow_type: 'credit' | 'debit';
    origin: string;
    client_id: string | null;
    project_id: string | null;
    task_id: string | null;
    amount: number;
    reference_date: string;
    description: string;
    payment_method: string;
    notes: string;
  }) => void;
}

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
  { value: 'cash', label: 'Dinheiro' },
];

const CREDIT_ORIGINS = [
  { value: 'manual', label: 'Lançamento Manual' },
  { value: 'project', label: 'Pagamento de Projeto' },
  { value: 'task', label: 'Pagamento de Atividade' },
  { value: 'advance', label: 'Adiantamento' },
  { value: 'refund', label: 'Reembolso Recebido' },
  { value: 'other', label: 'Outro' },
];

const DEBIT_ORIGINS = [
  { value: 'manual', label: 'Lançamento Manual' },
  { value: 'expense', label: 'Despesa Operacional' },
  { value: 'salary', label: 'Salário / Pró-labore' },
  { value: 'tax', label: 'Imposto / Taxa' },
  { value: 'tool', label: 'Ferramenta / Software' },
  { value: 'refund', label: 'Reembolso Pago' },
  { value: 'other', label: 'Outro' },
];

export default function CashFlowEntryModal({ type, clients, projects, onClose, onSave }: CashFlowEntryModalProps) {
  const [origin, setOrigin] = useState('manual');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceDate, setReferenceDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [notes, setNotes] = useState('');
  const [tasks, setTasks] = useState<{ id: string; title: string; value: number }[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const origins = type === 'credit' ? CREDIT_ORIGINS : DEBIT_ORIGINS;

  useEffect(() => {
    if (projectId) {
      setLoadingTasks(true);
      supabase
        .from('tasks')
        .select('id, title, value')
        .eq('project_id', projectId)
        .order('title')
        .then(({ data }) => {
          setTasks(data || []);
          setLoadingTasks(false);
        });
    } else {
      setTasks([]);
      setTaskId('');
    }
  }, [projectId]);

  // Preencher valor automaticamente ao selecionar tarefa
  useEffect(() => {
    if (taskId) {
      const task = tasks.find(t => t.id === taskId);
      if (task && task.value > 0) {
        setAmount(task.value.toString());
      }
    }
  }, [taskId, tasks]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) return;

    onSave({
      flow_type: type,
      origin,
      client_id: clientId || null,
      project_id: projectId || null,
      task_id: taskId || null,
      amount: numAmount,
      reference_date: referenceDate,
      description,
      payment_method: paymentMethod,
      notes,
    });
  };

  const isCredit = type === 'credit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${isCredit ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <i className={`text-base ${isCredit ? 'ri-arrow-up-circle-line text-emerald-600' : 'ri-arrow-down-circle-line text-red-500'}`}></i>
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold text-base">
                {isCredit ? 'Novo Crédito' : 'Novo Débito'}
              </h2>
              <p className="text-slate-400 text-xs">Lançamento manual no caixa</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Origem */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Origem</label>
            <select
              value={origin}
              onChange={e => setOrigin(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
            >
              {origins.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Valor + Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Valor (R$) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0,00"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Data *</label>
              <input
                type="date"
                value={referenceDate}
                onChange={e => setReferenceDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                required
              />
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Descrição *</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descreva o lançamento..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              required
            />
          </div>

          {/* Cliente + Projeto */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Cliente</label>
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
              >
                <option value="">Nenhum</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Projeto</label>
              <select
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
              >
                <option value="">Nenhum</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Atividade vinculada (só se projeto selecionado) */}
          {projectId && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Atividade vinculada (opcional)</label>
              <select
                value={taskId}
                onChange={e => setTaskId(e.target.value)}
                disabled={loadingTasks}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer disabled:bg-slate-50"
              >
                <option value="">Nenhuma</option>
                {tasks.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.title}{t.value > 0 ? ` — ${t.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Forma de pagamento */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Forma de pagamento</label>
            <select
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
            >
              {PAYMENT_METHODS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Notas adicionais..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!amount || parseFloat(amount) <= 0}
            className={`px-5 py-2 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:bg-slate-300 disabled:cursor-not-allowed ${
              isCredit ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            <i className={`mr-1.5 ${isCredit ? 'ri-arrow-up-circle-line' : 'ri-arrow-down-circle-line'}`}></i>
            Lançar {isCredit ? 'Crédito' : 'Débito'}
          </button>
        </div>
      </div>
    </div>
  );
}